import { useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { supabase } from './lib/supabase.js'

import HomePage          from './pages/HomePage.jsx'
import JoinPage          from './pages/JoinPage.jsx'
import LobbyPage         from './pages/LobbyPage.jsx'
import GamePage          from './pages/GamePage.jsx'
import SoloGamePage         from './pages/SoloGamePage.jsx'
import SoloPlayerStatsPage from './pages/SoloPlayerStatsPage.jsx'
import RacePage          from './pages/RacePage.jsx'
import StatsPage         from './pages/StatsPage.jsx'
import PodiumPage        from './pages/PodiumPage.jsx'
import AdminPage         from './pages/AdminPage.jsx'
import HostPage          from './pages/HostPage.jsx'
import HostDecksPage, { HostDeckShell } from './pages/HostDecksPage.jsx'
import DiscoverDecksPage from './pages/DiscoverDecksPage.jsx'
import NewDeckPage        from './pages/NewDeckPage.jsx'
import EditDeckPage       from './pages/EditDeckPage.jsx'
import GenerateDeckPage   from './pages/GenerateDeckPage.jsx'
import LoginPage         from './pages/LoginPage.jsx'
import NotFoundPage      from './pages/NotFoundPage.jsx'
import OptionsPage       from './pages/OptionsPage.jsx'
import AuthCallbackPage  from './pages/AuthCallbackPage.jsx'
import OnboardingPage    from './pages/OnboardingPage.jsx'
import ProfilePage       from './pages/ProfilePage.jsx'
import ShareRedirectPage from './pages/ShareRedirectPage.jsx'
import SharedQuizzesPage from './pages/SharedQuizzesPage.jsx'
import ProtectedRoute    from './components/ui/ProtectedRoute.jsx'
import ImpersonationBanner from './components/ui/ImpersonationBanner.jsx'
import UpdateNotifier   from './components/ui/UpdateNotifier.jsx'
import DeckDetail        from './components/admin/DeckDetail.jsx'
import QuestionEditor    from './components/admin/QuestionEditor.jsx'

import MarketingHomePage  from './marketing/pages/MarketingHomePage.jsx'
import ComparisonPage     from './marketing/pages/ComparisonPage.jsx'
import PricingPage        from './marketing/pages/PricingPage.jsx'
import FAQPage            from './marketing/pages/FAQPage.jsx'
import LegalPage          from './marketing/pages/LegalPage.jsx'
import PrivacyPage        from './marketing/pages/PrivacyPage.jsx'
import TermsPage          from './marketing/pages/TermsPage.jsx'
import FeaturesPage       from './marketing/pages/FeaturesPage.jsx'
import ForTeachersPage    from './marketing/pages/ForTeachersPage.jsx'
import BlogIndexPage      from './marketing/pages/BlogIndexPage.jsx'
import BlogPostPage       from './marketing/pages/BlogPostPage.jsx'

function AuthWatcher() {
  const navigate = useNavigate()
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/login', { replace: true })
      }
    })
    return () => subscription.unsubscribe()
  }, [navigate])
  return null
}

export default function App() {
  return (
    <>
    <AuthWatcher />
    <ImpersonationBanner />
    <UpdateNotifier />
    <Routes>
      {/* ── Marketing (public) ── */}
      <Route path="/"                  element={<MarketingHomePage />} />
      <Route path="/comparatif"        element={<ComparisonPage />} />
      <Route path="/tarifs"            element={<PricingPage />} />
      <Route path="/faq"               element={<FAQPage />} />
      <Route path="/mentions-legales"  element={<LegalPage />} />
      <Route path="/confidentialite"   element={<PrivacyPage />} />
      <Route path="/cgu"               element={<TermsPage />} />
      <Route path="/fonctionnalites"   element={<FeaturesPage />} />
      <Route path="/pour-les-profs"    element={<ForTeachersPage />} />
      <Route path="/blog"              element={<BlogIndexPage />} />
      <Route path="/blog/:slug"        element={<BlogPostPage />} />

      {/* ── App (ancienne /) déplacée vers /home ── */}
      <Route path="/home"              element={<ProtectedRoute><HomePage /></ProtectedRoute>} />

      {/* ── Auth ── */}
      <Route path="/login"             element={<LoginPage />} />
      <Route path="/auth/callback"     element={<AuthCallbackPage />} />
      <Route path="/onboarding"        element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/profile"           element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      {/* ── Partage de quiz ── */}
      <Route path="/share/:deckId"     element={<ShareRedirectPage />} />
      <Route path="/shared-quizzes"    element={<ProtectedRoute><SharedQuizzesPage /></ProtectedRoute>} />

      {/* ── Joueur ── */}
      <Route path="/join"              element={<JoinPage />} />
      <Route path="/join/:code"        element={<JoinPage />} />
      <Route path="/lobby/:code"       element={<LobbyPage />} />
      <Route path="/game/:code"        element={<GamePage />} />
      <Route path="/solo/:code"        element={<SoloGamePage />} />
      <Route path="/solo-stats/:code"  element={<SoloPlayerStatsPage />} />

      {/* ── Course ── */}
      <Route path="/race/:code"        element={<ProtectedRoute><RacePage /></ProtectedRoute>} />
      <Route path="/spectator/:code"   element={<RacePage />} />
      <Route path="/stats/:code"       element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
      <Route path="/podium/:code"      element={<ProtectedRoute><PodiumPage /></ProtectedRoute>} />
      <Route path="/options/:code"     element={<ProtectedRoute><OptionsPage /></ProtectedRoute>} />

      {/* ── Admin / decks (pages dédiées, avant le wildcard /admin/*) ── */}
      <Route path="/admin/decks/new"                element={<ProtectedRoute requiredRole="admin"><NewDeckPage navBase="/admin/decks" /></ProtectedRoute>} />
      <Route path="/admin/decks/:deckId/edit"       element={<ProtectedRoute requiredRole="admin"><EditDeckPage navBase="/admin/decks" /></ProtectedRoute>} />
      <Route path="/admin/decks/:deckId/generate"   element={<ProtectedRoute requiredRole="admin"><GenerateDeckPage navBase="/admin/decks" /></ProtectedRoute>} />

      {/* ── Admin ── */}
      <Route path="/admin/*"           element={<ProtectedRoute requiredRole="admin"><AdminPage /></ProtectedRoute>} />

      {/* ── Host / decks ── */}
      <Route path="/host/decks/new"                element={<ProtectedRoute><NewDeckPage /></ProtectedRoute>} />
      <Route path="/host/decks/:deckId/edit"       element={<ProtectedRoute><EditDeckPage /></ProtectedRoute>} />
      <Route path="/host/decks/:deckId/generate"   element={<ProtectedRoute><GenerateDeckPage /></ProtectedRoute>} />
      <Route path="/host/decks"                    element={<ProtectedRoute><HostDecksPage /></ProtectedRoute>} />
      <Route path="/host/decks/:deckId"            element={<ProtectedRoute><HostDeckShell><DeckDetail basePath="/host/decks" darkMode /></HostDeckShell></ProtectedRoute>} />
      <Route path="/host/decks/:deckId/questions"  element={<ProtectedRoute><HostDeckShell><QuestionEditor basePath="/host/decks" darkMode /></HostDeckShell></ProtectedRoute>} />

      {/* ── Discover / quiz communautaires ── */}
      <Route path="/host/discover"                          element={<ProtectedRoute><DiscoverDecksPage /></ProtectedRoute>} />
      <Route path="/host/discover/:deckId/questions"        element={<ProtectedRoute><HostDeckShell rootPath="/host/discover" rootLabel="Découvrir"><QuestionEditor basePath="/host/discover" darkMode readOnly /></HostDeckShell></ProtectedRoute>} />

      <Route path="/host/:code"                    element={<ProtectedRoute><HostPage /></ProtectedRoute>} />

      <Route path="*"                  element={<NotFoundPage />} />
    </Routes>
    </>
  )
}
